import type { ReactNode } from "react";
import { fmtMoney, fmtSignedMoney, pnlTone } from "../lib/format.js";
import { AccessErrorCard } from "./Gate.js";
import { entitlementOf, isSignInRequired } from "../lib/api.js";

/** Shared presentational primitives for the terminal UI — consistent spacing,
 * borders, and typography so pages read as one system. */

/* ------------------------------------------------------------------ surfaces */

/** Elevated card surface: 1px border on a slightly-raised panel over near-black. */
export function Card({
  children,
  className = "",
  as: As = "section",
}: {
  children: ReactNode;
  className?: string;
  as?: "section" | "div";
}) {
  return (
    <As className={`rounded-lg border border-border bg-panel ${className}`}>{children}</As>
  );
}

/** Section header inside a card: small uppercase label + optional right slot. */
export function SectionHeader({
  title,
  meta,
  right,
}: {
  title: ReactNode;
  meta?: ReactNode;
  right?: ReactNode;
}) {
  return (
    <div className="flex items-baseline gap-2 border-b border-border px-4 py-2.5">
      <h2 className="text-[11px] font-semibold uppercase tracking-wider text-dim">{title}</h2>
      {meta && <span className="text-[11px] text-faint">{meta}</span>}
      {right && <div className="ml-auto flex items-center gap-2">{right}</div>}
    </div>
  );
}

/* --------------------------------------------------------------------- stats */

/** Plain money value, tabular. */
export function Money({ value, whole }: { value: number | null | undefined; whole?: boolean }) {
  return <span className="nums">{fmtMoney(value, { whole })}</span>;
}

/** Signed, colored P&L value (green up / red down / muted at zero). */
export function Pnl({
  value,
  className = "",
}: {
  value: number | null | undefined;
  className?: string;
}) {
  return (
    <span className={`nums ${pnlTone(value)} ${className}`}>{fmtSignedMoney(value)}</span>
  );
}

/* ------------------------------------------------------------------- states */

export function Loading({ label = "loading…" }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-6 text-dim">
      <span className="inline-block h-2 w-2 animate-ping rounded-full bg-blue" />
      <span className="text-xs">{label}</span>
    </div>
  );
}

/**
 * A single shimmering placeholder block. Size/shape it with className (h-*, w-*, rounded-*).
 * The shimmer + reduced-motion handling live in the `.skeleton` class (index.css). The whole
 * point is to reserve the SAME footprint the real content will take, so nothing jumps when it
 * loads — always give it a height/width close to the final element's.
 */
export function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`skeleton ${className}`} aria-hidden />;
}

/** Skeleton table rows — used instead of a spinner-in-the-void while loading. */
export function SkeletonRows({ rows = 6, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="divide-y divide-border/60">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-4 px-4 py-2.5">
          {Array.from({ length: cols }).map((_, c) => (
            <div key={c} style={{ width: c === 0 ? "38%" : `${12 + ((r + c) % 3) * 6}%` }}>
              <Skeleton className="h-3.5 w-full" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  // "Needs sign-in" / "needs an upgrade" aren't errors to the visitor — show the gate instead.
  if (isSignInRequired(error) || entitlementOf(error)) return <AccessErrorCard error={error} />;
  const msg = error instanceof Error ? error.message : String(error);
  const code =
    error && typeof error === "object" && "code" in error
      ? String((error as { code: unknown }).code)
      : null;
  return (
    <div className="m-4 rounded-lg border border-down/40 bg-down/5 px-4 py-3 text-xs">
      <div className="mb-1 font-semibold text-down">error{code ? ` · ${code}` : ""}</div>
      <div className="text-dim">{msg}</div>
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-2 rounded border border-border-2 px-2 py-0.5 text-[11px] text-ink hover:bg-raised"
        >
          retry
        </button>
      )}
    </div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="m-4 rounded-lg border border-dashed border-border bg-panel-2 px-4 py-8 text-center">
      <div className="text-xs text-dim">{title}</div>
      {hint && <div className="mt-1 text-[11px] text-faint">{hint}</div>}
    </div>
  );
}

/* -------------------------------------------------------------------- badges */

export function Badge({
  children,
  tone = "dim",
  title,
}: {
  children: ReactNode;
  tone?: "dim" | "amber" | "blue" | "up" | "down";
  title?: string;
}) {
  const tones: Record<string, string> = {
    dim: "border-border-2 text-dim",
    amber: "border-amber/50 text-amber",
    blue: "border-blue/50 text-blue",
    up: "border-up/50 text-up",
    down: "border-down/50 text-down",
  };
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

/**
 * Status pill for venue connection state — a filled dot + label. `active`/`connected`
 * read green; `pending` amber; `inactive`/anything else muted.
 */
export function StatusPill({ status }: { status?: string | null }) {
  const s = (status ?? "").toLowerCase();
  const good = s === "active" || s === "connected" || s === "all_filled" || s === "filled";
  const warn =
    s === "pending" || s === "open" || s === "partial" || s.includes("partial") || s === "resting";
  const bad =
    s === "rejected" || s === "failed" || s === "cancelled" || s === "canceled" || s === "error";
  const cfg = good
    ? { dot: "bg-up", text: "text-up" }
    : bad
      ? { dot: "bg-down", text: "text-down" }
      : warn
        ? { dot: "bg-amber", text: "text-amber" }
        : { dot: "bg-faint", text: "text-faint" };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide ${cfg.text}`}>
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
      {status ?? "—"}
    </span>
  );
}

export { fmtMoney, fmtSignedMoney };
